import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { MessageSquare, Search, X, Eye, ArrowRight, Plus } from 'lucide-react';

const SORTS = [
  { value: 'recent', label: 'Most Recent' },
  { value: 'answered', label: 'Most Answered' },
  { value: 'unanswered', label: 'Unanswered' },
];

const CommunityBrowse = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(params.get('search') || '');

  const category = params.get('category') || '';
  const search = params.get('search') || '';
  const sort = params.get('sort') || 'recent';
  const page = parseInt(params.get('page') || '1', 10);

  useEffect(() => {
    document.title = 'Community Questions | Ask a Social Worker | I Need A Social Worker';
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/community/categories`).then((r) => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (category) q.set('category', category);
      if (search) q.set('search', search);
      q.set('sort', sort);
      q.set('page', page);
      const res = await axios.get(`${API_URL}/community/questions?${q.toString()}`);
      setQuestions(res.data.questions || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (e) { setQuestions([]); } finally { setLoading(false); }
  }, [category, search, sort, page]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const setParam = (key, val) => {
    const p = new URLSearchParams(params);
    if (val) p.set(key, val); else p.delete(key);
    if (key !== 'page') p.delete('page');
    setParams(p);
  };

  const submitSearch = (e) => { e.preventDefault(); setParam('search', searchInput); };
  const activeCat = categories.find((c) => c.slug === category);

  return (
    <div className="min-h-screen bg-[#F7F5EF]">
      <section className="bg-primary pt-24 pb-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <Link to="/community/ask-a-social-worker" className="text-secondary text-sm hover:underline">← Ask a Social Worker</Link>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold mt-2">{activeCat ? activeCat.label : 'Community Questions'}</h1>
          <p className="text-white/80 mt-2">Search before posting — your question may already be answered.</p>
          <form onSubmit={submitSearch} className="mt-5 flex flex-col sm:flex-row gap-2 max-w-xl">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input className="pl-9 bg-white text-gray-900" placeholder="Search questions" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} data-testid="community-search-input" />
            </div>
            <Button type="submit" className="bg-secondary hover:bg-secondary-hover text-white" data-testid="community-search-btn">Search</Button>
            <Button type="button" onClick={() => navigate('/community/ask')} className="bg-white text-primary hover:bg-white/90" data-testid="browse-ask-btn"><Plus className="w-4 h-4 mr-1" /> Ask</Button>
          </form>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Category chips */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={() => setParam('category', '')} className={`text-sm px-3 py-1.5 rounded-full border ${!category ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:border-secondary'}`} data-testid="chip-all">All</button>
          {categories.map((c) => (
            <button key={c.slug} onClick={() => setParam('category', c.slug)} className={`text-sm px-3 py-1.5 rounded-full border ${category === c.slug ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:border-secondary'}`} data-testid={`chip-${c.slug}`}>{c.label}</button>
          ))}
        </div>

        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex gap-2">
            {SORTS.map((s) => (
              <button key={s.value} onClick={() => setParam('sort', s.value)} className={`text-sm px-3 py-1 rounded-md ${sort === s.value ? 'bg-secondary text-white' : 'text-gray-500 hover:text-primary'}`} data-testid={`sort-${s.value}`}>{s.label}</button>
            ))}
          </div>
          <span className="text-sm text-gray-400">{total} question{total === 1 ? '' : 's'}</span>
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-500">Loading questions…</div>
        ) : questions.length === 0 ? (
          <div className="bg-white rounded-2xl border p-10 text-center" data-testid="no-questions">
            <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No questions here yet.</p>
            <p className="text-gray-400 text-sm mt-1">Be the first to start the conversation.</p>
            <Button onClick={() => navigate('/community/ask')} className="mt-4 bg-secondary hover:bg-secondary-hover text-white" data-testid="empty-ask-btn">Ask a Question</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {questions.map((q) => (
              <Link key={q.question_id} to={`/community/questions/${q.question_id}`} data-testid={`question-card-${q.question_id}`}
                className="block bg-white rounded-2xl border p-5 hover:border-secondary hover:shadow-sm transition-all">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <span className="inline-block text-xs font-semibold text-secondary uppercase tracking-wide">{q.category}</span>
                    <h3 className="font-serif text-lg font-bold text-primary leading-tight mt-0.5">{q.title}</h3>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{q.body}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-gray-400">
                      <span>{q.author_display}</span>
                      {q.published_at && <span>{formatDate(q.published_at)}</span>}
                      <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> {q.answer_count || 0}</span>
                      <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {q.views || 0}</span>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-300 flex-shrink-0 mt-1" />
                </div>
              </Link>
            ))}
          </div>
        )}

        {(category || search) && (
          <button onClick={() => setParams(new URLSearchParams())} className="text-sm text-gray-500 hover:text-primary flex items-center gap-1 mt-4" data-testid="clear-community-filters"><X className="w-4 h-4" /> Clear filters</button>
        )}

        {pages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <Button variant="outline" disabled={page <= 1} onClick={() => setParam('page', String(page - 1))} data-testid="prev-page">Previous</Button>
            <span className="px-4 py-2 text-sm text-gray-500">Page {page} of {pages}</span>
            <Button variant="outline" disabled={page >= pages} onClick={() => setParam('page', String(page + 1))} data-testid="next-page">Next</Button>
          </div>
        )}
      </section>
    </div>
  );
};

export default CommunityBrowse;
